import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getOrderItemRepresentation,
  getTrackFee,
} from "../../../../store/slices/OrderSlice/OrderSlice";
import { saveCartItem } from "../../../../store/slices/CartSlice/CartSlice";
import {
  buildAlternativeCartItemPayload,
  convertImageNameToWeb,
  formatMoney,
} from "../../../../helpers/helperFunctions";
import { Link } from "react-router-dom";
import { getTrackMakesAndModels } from "../../../../store/slices/ProductFinder/ProductFinderSlice";

export default function OrderItems() {
  const dispatch = useDispatch();

  const { loadedOrderItems: orderItems, trackFee } = useSelector(
    (state) => state.orders
  );
  const { cart, cartItems } = useSelector((state) => state.cart);
  const { prepaidWeight } = useSelector((state) => state.customer);
  const [swappingProductCode, setSwappingProductCode] = useState(null);

  useEffect(() => {
    dispatch(getOrderItemRepresentation());
    dispatch(getTrackFee());
  }, [dispatch]);

  const fetchTrackMatches = (productCode) => {
    dispatch(getTrackMakesAndModels({ product_code: productCode }));
  };

  const handleSwapToAlternative = async (item) => {
    const cartItem = cartItems.find(
      (ci) => ci.product_code === item.product.product_code
    );
    if (!cartItem || !item.alternative) return;

    setSwappingProductCode(item.product.product_code);
    await dispatch(
      saveCartItem({
        cartItem: buildAlternativeCartItemPayload(cartItem, item.alternative),
      })
    );
    await dispatch(getOrderItemRepresentation());
    setSwappingProductCode(null);
  };

  return (
    <div className="flex flex-col p-8 ">
      {/* Warning banner for track fee */}
      {trackFee > 0 && (
        <div className="w-full mb-4 p-3 bg-red-50 border-l-4 border-red-400 rounded-md">
          <div className="flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-red-500 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p className="text-sm font-medium text-red-800">
              Warning: A ${trackFee.toFixed(2)} fee will be applied for ordering
              only a single track. To avoid this fee, please order at least two
              tracks or make sure your total order weight is greater than{" "}
              {prepaidWeight} lbs.
              <br />
              <span className="font-normal">
                Current Weight: {cart.total_weight} lbs
              </span>
            </p>
          </div>
        </div>
      )}
      <ul className="mt-6 mx-6 divide-y divide-gray-200 border-gray-200 text-sm font-medium text-gray-500">
        {orderItems?.map((item) => {
          if (!item?.product) {
            return null; // Skip items without product information
          }
          return (
            <li
              key={item.product.product_code}
              className="flex flex-col space-y-4 py-6"
            >
              <div className="flex space-x-6">
                {item?.product?.image_name && (
                  <img
                    alt={item?.product?.image_name}
                    src={
                      process.env.REACT_APP_IMAGE_URL +
                      convertImageNameToWeb(item?.product?.image_name)
                    }
                    className="h-32 w-32 flex-none rounded-md bg-gray-100 object-contain p-2"
                  />
                )}

                <div className="flex-auto space-y-1">
                  <h3 className="flex text-gray-900">
                    <p>{item.product.description}</p>
                    <Link
                      to="/view-cart"
                      className="ml-1 text-[var(--primary-color)] hover:text-[var(--secondary-color)] hover:cursor-pointer"
                    >
                      - Edit
                    </Link>
                  </h3>
                  <div className="flex gap-2 items-center">
                    <p>
                      {item.item_price
                        ? `${formatMoney(item.item_price)}`
                        : "-"}{" "}
                      x {item.qty_ordered}
                    </p>
                    {/* <CartItemCounter cartItem={item} /> */}
                  </div>
                  <div className="flex gap-2 items-center">
                    <p>
                      {item.tariff_amount
                        ? `${formatMoney(item.tariff_amount)}`
                        : "-"}{" "}
                      x {item.qty_ordered}  Tariff
                    </p>
                    {/* <CartItemCounter cartItem={item} /> */}
                  </div>
                  <div className="flex gap-2 items-center">
                    <p>
                      {item.product.shipping_weight
                        ? `${item.product.shipping_weight} lbs`
                        : "-"}{" "}
                      x {item.qty_ordered}
                    </p>
                  </div>
                  <p>{item.product.xref_code}</p>
                  <p>{item.product.product_code}</p>
                  {item.qty_backordered > 0 && (
                    <div className="flex flex-col gap-2 rounded-md bg-amber-50 border-l-4 border-amber-400 px-3 py-2 mt-1">
                      <div className="flex items-center gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-amber-500 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                        <p className="text-sm font-medium text-amber-800">
                          {item.qty_backordered} of {item.qty_ordered} will be
                          backordered
                        </p>
                      </div>
                      {item.alternative && (
                        <div className="flex items-center gap-2 pl-7">
                          <p className="text-sm text-amber-700">
                            {item.alternative.description} is in stock
                            ({item.alternative.available_qty} available) --
                            switch to avoid the backorder?
                          </p>
                          <button
                            type="button"
                            onClick={() => handleSwapToAlternative(item)}
                            disabled={
                              swappingProductCode === item.product.product_code
                            }
                            className="rounded-md bg-[var(--primary-color)] px-2 py-1 text-xs font-semibold text-white shadow-xs hover:bg-[var(--secondary-color)] disabled:opacity-50 whitespace-nowrap"
                          >
                            {swappingProductCode === item.product.product_code
                              ? "Swapping..."
                              : "Swap Item"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {["16", "17"].includes(
                    item.product.product_code.substring(0, 2)
                  ) && (
                    <p
                      onClick={() =>
                        fetchTrackMatches(item.product.product_code)
                      }
                      className="text-[var(--primary-color)] hover:cursor-pointer hover:text-[var(--secondary-color)]"
                    >
                      What does this fit?
                    </p>
                  )}
                </div>
                <div>
                  <p className="flex-none font-medium text-gray-900 text-right">
                    {item.item_price
                      ? `${formatMoney(item.item_price * item.qty_ordered + (item.tariff_amount || 0) * item.qty_ordered)}`
                      : "-"}
                  </p>
                  <p className="flex-none font-medium text-gray-900 text-right">
                    {item.product.shipping_weight
                      ? `${(item.product.shipping_weight * item.qty_ordered).toFixed(2)} lbs`
                      : "-"}
                  </p>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
